import { useTranslation } from "react-i18next";
import { trainings } from "@/mocks/homeData";
import { useEffect, useRef, useState } from "react";

interface CapacitacionesProps {
  onOpenModal: (type: string) => void;
}

export default function Capacitaciones({ onOpenModal }: CapacitacionesProps) {
  const { t } = useTranslation("common");
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section id="capacitaciones" ref={sectionRef} className="relative py-12 md:py-20 bg-[#F0F4FA] overflow-hidden">
      {/* Humanized background */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://readdy.ai/api/search-image?query=Diverse%20group%20of%20professionals%20attending%20a%20cybersecurity%20training%20workshop%20in%20a%20bright%20modern%20classroom%2C%20engaged%20learners%20at%20desks%20with%20laptops%2C%20friendly%20collaborative%20atmosphere%2C%20natural%20daylight%2C%20editorial%20photography%2C%20human%20centered%2C%20light%20neutral%20tones&width=1600&height=900&seq=training-section-human-bg-01&orientation=landscape"
          srcSet="https://readdy.ai/api/search-image?query=Diverse%20group%20of%20professionals%20attending%20a%20cybersecurity%20training%20workshop%20in%20a%20bright%20modern%20classroom%2C%20engaged%20learners%20at%20desks%20with%20laptops%2C%20friendly%20collaborative%20atmosphere%2C%20natural%20daylight%2C%20editorial%20photography%2C%20human%20centered%2C%20light%20neutral%20tones&width=1280&height=720&seq=training-section-human-bg-01&orientation=landscape 1280w, https://readdy.ai/api/search-image?query=Diverse%20group%20of%20professionals%20attending%20a%20cybersecurity%20training%20workshop%20in%20a%20bright%20modern%20classroom%2C%20engaged%20learners%20at%20desks%20with%20laptops%2C%20friendly%20collaborative%20atmosphere%2C%20natural%20daylight%2C%20editorial%20photography%2C%20human%20centered%2C%20light%20neutral%20tones&width=1600&height=900&seq=training-section-human-bg-01&orientation=landscape 1600w"
          sizes="100vw"
          alt=""
          aria-hidden="true"
          className="w-full h-full object-cover object-center"
          loading="lazy"
          decoding="async"
        />
        <div className="absolute inset-0 bg-[#F0F4FA]/80" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#F0F4FA]/92 via-[#F0F4FA]/58 to-[#F0F4FA]/92" />
      </div>
      <div className="absolute top-0 left-0 right-0 h-[1px] bg-[#D0D8E8]" />
      {/* Transition gradient toward the next (dark Datacenter) section */}
      <div className="absolute bottom-0 left-0 right-0 h-24 md:h-32 bg-gradient-to-b from-transparent to-[#0A1220] z-[5] pointer-events-none" />

      <div className="w-full px-4 sm:px-6 lg:px-10 relative z-10">
        <div
          className={`text-center mb-10 md:mb-12 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <h2 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-[#1A2B4A] mb-2">
            {t("training.title")}
          </h2>
          <p className="text-sm text-[#43506B] max-w-lg mx-auto leading-relaxed">
            {t("training.subtitle")}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 max-w-6xl mx-auto">
          {trainings.map((item, idx) => (
            <div
              key={item.id}
              className={`transition-all duration-700 will-change-transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
              style={{ transitionDelay: `${150 + idx * 100}ms` }}
            >
              <InteractiveTrainingCard item={item} idx={idx} onOpenModal={onOpenModal} t={t} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function InteractiveTrainingCard({
  item,
  idx,
  onOpenModal,
  t,
}: {
  item: {
    id: string;
    categoryKey: string;
    titleKey: string;
    modalType: string;
    image: string;
  };
  idx: number;
  onOpenModal: (type: string) => void;
  t: (key: string) => string;
}) {
  const [isHovered, setIsHovered] = useState(false);

  const students = [340, 280, 195, 420][idx];
  const hours = [40, 32, 24, 56][idx];
  const rating = [4.8, 4.9, 4.7, 4.9][idx];

  return (
    <div
      className="group relative rounded-xl border border-[#D0D8E8] bg-white shadow-sm hover:shadow-xl hover:border-[#4A7DBF]/30 overflow-hidden transition-all duration-500 cursor-pointer will-change-transform"
      onClick={() => onOpenModal(item.modalType)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Image area */}
      <div className="relative h-36 md:h-40 overflow-hidden">
        <img
          src={item.image}
          alt={t(item.titleKey)}
          width={350}
          height={260}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
        />
        <span className="absolute top-3 left-3 z-20 inline-flex items-center px-3 py-1 rounded-full bg-white/90 border border-[#C8D6E8] text-[10px] font-label font-semibold text-[#4A7DBF] uppercase tracking-wider">
          {t(item.categoryKey)}
        </span>

        {/* Hover overlay with stats */}
        <div
          className={`absolute inset-0 bg-gradient-to-t from-[#1A2B4A]/90 via-[#1A2B4A]/40 to-transparent flex flex-col justify-end p-4 transition-all duration-500 ${isHovered ? "opacity-100" : "opacity-0"}`}
        >
          <div className="flex items-center gap-4">
            <div className="text-center">
              <p className="text-lg font-heading font-bold text-white">{students}</p>
              <p className="text-[9px] text-white/60 font-label uppercase tracking-wider">{t("training.students")}</p>
            </div>
            <div className="w-px h-8 bg-white/20" />
            <div className="text-center">
              <p className="text-lg font-heading font-bold text-white">{hours}h</p>
              <p className="text-[9px] text-white/60 font-label uppercase tracking-wider">{t("training.duration")}</p>
            </div>
            <div className="w-px h-8 bg-white/20" />
            <div className="text-center">
              <p className="text-lg font-heading font-bold text-white">{rating}</p>
              <p className="text-[9px] text-white/60 font-label uppercase tracking-wider">{t("training.rating")}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 md:p-5">
        <h3 className="font-heading font-semibold text-sm text-[#1A2B4A] mb-3 group-hover:text-[#4A7DBF] transition-colors duration-300 line-clamp-2">
          {t(item.titleKey)}
        </h3>

        {/* Progress bar */}
        <div className="mb-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[9px] font-label text-[#7A8BA8] uppercase tracking-wider">{t("training.demand")}</span>
            <span className="text-[9px] font-label text-[#4A7DBF] font-semibold">{t("training.demandHigh")}</span>
          </div>
          <div className="h-1 rounded-full bg-[#E8EDF5] overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-[#4A7DBF] to-[#6B9BD4] transition-all duration-700"
              style={{ width: isHovered ? "85%" : "60%", transitionDelay: "100ms" }}
            />
          </div>
        </div>

        <div className="flex items-center justify-between pt-3 border-t border-[#E8EDF5]">
          <span className="text-[11px] font-label font-semibold text-[#4A7DBF] uppercase tracking-wider">
            {t("training.viewCourse")}
          </span>
          <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#4A7DBF]/10 group-hover:bg-[#4A7DBF] transition-all duration-300">
            <i className="ri-arrow-right-line text-xs text-[#4A7DBF] group-hover:text-white transition-colors" />
          </div>
        </div>
      </div>
    </div>
  );
}